import React, { useCallback, MouseEvent, memo, useMemo } from 'react';
import * as Styled from './styled';
import * as GStyled from '../../../../styles/components';
import { ReportField } from '../../../../models/reportField';
import { RuleBuilderInput } from '..';
import {
  ButtonGroup,
  SelectOption,
  Select,
} from '@leasequery/lq.react-component-library';
import RuleValueInput from './RuleValueInput';
import { noop } from '../../../../util';
import {
  COMPARISON_RULE_SELECT_OPTIONS,
  LOGICAL_RULE_SELECT_OPTIONS_ARRAY,
  LOGICAL_RULE_SELECT_OPTIONS,
} from '../../../../constants';
import { Icon } from '../../../../components';

interface RuleInputFormProps {
  reportField: ReportField;
  rule: RuleBuilderInput;
  hasLogicalRule: boolean;
  onRemoveClick?: () => void;
  onChange?: (rule: RuleBuilderInput) => void;
}

function RuleInputForm({
  reportField,
  rule,
  hasLogicalRule,
  onRemoveClick,
  onChange,
}: RuleInputFormProps): JSX.Element {
  const comparisonRuleOptions =
    COMPARISON_RULE_SELECT_OPTIONS[reportField.fieldType];
  const comparisonRuleOptionsArray: SelectOption<string>[] = useMemo(
    () => Object.values(comparisonRuleOptions),
    [comparisonRuleOptions],
  );

  const editingRule: RuleBuilderInput = useMemo(
    () => ({
      logicalRule: LOGICAL_RULE_SELECT_OPTIONS_ARRAY[0]
        .value as RuleBuilderInput['logicalRule'],
      comparisonRule: comparisonRuleOptionsArray[0]
        .value as RuleBuilderInput['comparisonRule'],
      value1: '',
      value2: '',
      ...rule,
    }),
    [comparisonRuleOptionsArray, rule],
  );

  const logicalRule = LOGICAL_RULE_SELECT_OPTIONS[editingRule.logicalRule];
  const comparisonRule = comparisonRuleOptions[editingRule.comparisonRule];

  const logicalRuleClickHandler = useCallback(
    (_e: MouseEvent, option: SelectOption<string>): void => {
      onChange({
        ...editingRule,
        logicalRule: option.value as RuleBuilderInput['logicalRule'],
      });
    },
    [onChange, editingRule],
  );

  const comparisonRuleSelectHandler = useCallback(
    (option: SelectOption<string>): void => {
      onChange({
        ...editingRule,
        comparisonRule: option.value as RuleBuilderInput['comparisonRule'],
      });
    },
    [onChange, editingRule],
  );

  const value1ChangeHandler = useCallback(
    (val: string): void => {
      onChange({
        ...editingRule,
        value1: val,
      });
    },
    [editingRule, onChange],
  );
  const value2ChangeHandler = useCallback(
    (val: string): void => {
      onChange({
        ...editingRule,
        value2: val,
      });
    },
    [editingRule, onChange],
  );

  return (
    <Styled.RuleInputForm hasLogicalColumn={hasLogicalRule}>
      {hasLogicalRule && (
        <Styled.RoleButtonGroup>
          <ButtonGroup
            options={LOGICAL_RULE_SELECT_OPTIONS_ARRAY}
            selectedOption={logicalRule}
            onClick={logicalRuleClickHandler}
            name="logicalRule"
          />
        </Styled.RoleButtonGroup>
      )}
      <Select
        name="comparisonRule"
        options={comparisonRuleOptionsArray}
        onChange={comparisonRuleSelectHandler}
        value={comparisonRule}
      />
      <RuleValueInput
        reportField={reportField}
        inputType={reportField.fieldType}
        comparisonRule={
          comparisonRule.value as RuleBuilderInput['comparisonRule']
        }
        value1={editingRule.value1}
        value1Err={editingRule.value1Err}
        onValue1Change={value1ChangeHandler}
        value2={editingRule.value2}
        value2Err={editingRule.value2Err}
        onValue2Change={value2ChangeHandler}
      />
      <GStyled.FlatButton onClick={onRemoveClick}>
        <Icon name="times" />
      </GStyled.FlatButton>
    </Styled.RuleInputForm>
  );
}

RuleInputForm.defaultProps = {
  onChange: noop,
};

export default memo(RuleInputForm);
